import React, { useState } from 'react';
import { Brain, HelpCircle, Image, Layers, Plus } from 'lucide-react';
import PillTabs from '../../../components/common/PillTabs';
import { useTheme } from '../../../components/common/Theme';
import { ghostBtn, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import LLMs from './LLMs';
import Embeddings from './Embeddings';
import InvokeProfiles from './invoke/InvokeProfiles';
import InvokeHelpDialog from './invoke/InvokeHelpDialog';

type TabId = 'llm' | 'embeddings' | 'invoke';

const TABS = [
  { id: 'llm' as const, label: 'LLM', icon: Brain },
  { id: 'embeddings' as const, label: 'Embeddings', icon: Layers },
  { id: 'invoke' as const, label: 'Invoke', icon: Image },
];

const ADD_LABEL: Record<TabId, string> = {
  llm: 'Add New LLM Configuration',
  embeddings: 'Add New Embedding Provider',
  invoke: 'Add Invoke Profile',
};

const LLMRouting: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [tab, setTab] = useState<TabId>('llm');
  const [addTick, setAddTick] = useState(0);
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className={`w-full min-w-0 -m-3 min-h-[calc(100vh-1.5rem)] ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
      <header className={`px-6 pt-6 ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
        <p className={`m-0 text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`}>
          Configurations
        </p>
        <h1 className={`mt-1 text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-neutral-50'}`}>
          LLM Routing
        </h1>
        <p className={`mt-1.5 max-w-xl text-[13px] ${isLight ? 'text-slate-500' : 'text-neutral-400'}`}>
          LLM, embedding, and invoke profiles for chat or image models.
        </p>
      </header>
      <div className="px-6 py-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PillTabs
            isLight={isLight}
            ariaLabel="LLM routing sections"
            value={tab}
            items={TABS}
            onChange={setTab}
          />
          <div className="flex items-center gap-2">
            <button type="button" className={`${primaryBtn()} rounded-full`} onClick={() => setAddTick((value) => value + 1)}>
              <Plus size={14} /> {ADD_LABEL[tab]}
            </button>
            {tab === 'invoke' ? (
              <button type="button" className={`${ghostBtn(isLight)} rounded-full`} aria-label="Invoke profile help" onClick={() => setShowHelp(true)}>
                <HelpCircle size={16} />
              </button>
            ) : null}
          </div>
        </div>
        <div className="min-w-0">
          {tab === 'llm' ? <LLMs isLight={isLight} requestAdd={addTick} /> : null}
          {tab === 'embeddings' ? <Embeddings isLight={isLight} requestAdd={addTick} /> : null}
          {tab === 'invoke' ? <InvokeProfiles isLight={isLight} requestAdd={addTick} /> : null}
        </div>
      </div>
      {showHelp ? <InvokeHelpDialog isLight={isLight} onClose={() => setShowHelp(false)} /> : null}
    </div>
  );
};

export default LLMRouting;
